import React from 'react';
import Footer from '../components/Footer';
import Header from '../components/Header';
import SEOHead from '../components/SEO/SEOHead';
import { useBrand } from '../components/BrandProvider';

const CGVPage = () => {
  const brand = useBrand();
  
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <SEOHead 
        title="Conditions Générales de Vente (CGV)"
        description={`Consultez les conditions générales de vente de ${brand.name} : commande, paiement, livraison, garanties, droit de rétractation et mentions légales.`}
        url="/cgv"
        noindex={false}
      />
      
      <Header />

      {/* Main Content */}
      <main className="flex-grow">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-white rounded-lg shadow-md p-8 md:p-12">
            <h1 className="text-3xl md:text-4xl font-bold text-brand mb-2 text-center">
              CONDITIONS GÉNÉRALES DE VENTE
            </h1>
            <p className="text-center text-xl text-gray-600 mb-2">
              {brand.name.toUpperCase()}
            </p>
            <p className="text-center text-sm text-gray-500 mb-8">
              Mise à jour : 3 novembre 2025
            </p>

            <div className="space-y-8 text-gray-700 leading-relaxed">
              {/* Section 1 */}
              <section>
                <h2 className="text-2xl font-bold text-brand mb-3">1. PRÉAMBULE</h2>
                <p>
                  Les présentes Conditions Générales de Vente (ci-après « CGV ») s'appliquent à toute commande passée sur le site <a href={brand.domain} className="text-brand hover:underline">{brand.domain.replace('https://', '')}</a> (ci-après « le Site »). Toute commande implique l'acceptation pleine et entière des présentes CGV par le Client. {brand.name.toUpperCase()} se réserve le droit de modifier ses CGV à tout moment ; les CGV applicables sont celles en vigueur à la date de la commande.
                </p>
              </section>

              {/* Section 2 */}
              <section>
                <h2 className="text-2xl font-bold text-brand mb-3">2. IDENTIFICATION DU VENDEUR</h2>
                <div className="space-y-1">
                  <p><strong>Vendeur :</strong> {brand.name.toUpperCase()}</p>
                  <p><strong>Forme :</strong> entreprise individuelle</p>
                  <p><strong>SIRET :</strong> 380 703 256 00046</p>
                  <p><strong>Code APE :</strong> 47.91A</p>
                  <p><strong>Adresse :</strong> {brand.contact.address.street} – {brand.contact.address.postalCode} {brand.contact.address.city} – France</p>
                  <p><strong>Téléphone :</strong> {brand.contact.phone}</p>
                  <p><strong>E-mail :</strong> {brand.contact.email}</p>
                  <p><strong>Site :</strong> <a href={brand.domain} className="text-brand hover:underline">{brand.domain}</a></p>
                </div>
              </section>

              {/* Section 3 */}
              <section>
                <h2 className="text-2xl font-bold text-brand mb-3">3. OBJET</h2>
                <p>
                  Les présentes CGV ont pour objet de définir les droits et obligations des parties dans le cadre de la vente en ligne de toiles de stores bannes sur mesure, lambrequins, accessoires de stores, éléments personnalisés (flocage, lambrequins lumineux, marquage) et, plus généralement, de tout produit proposé sur le Site. Les produits vendus sont, pour l'essentiel, fabriqués à la demande et selon les spécifications du Client.
                </p>
              </section>

              {/* Section 4 */}
              <section>
                <h2 className="text-2xl font-bold text-brand mb-3">4. CARACTÉRISTIQUES DES PRODUITS</h2>
                <p>
                  Les produits sont décrits et présentés avec le plus grand soin. Les photographies, nuanciers et visuels n'ont toutefois pas de valeur contractuelle, notamment en raison des différences possibles d'affichage écran/impression. Les produits sur mesure sont réalisés selon les cotes, options et coloris saisis par le Client : toute erreur de mesure ou de choix du Client ne pourra engager la responsabilité de ${brand.name.toUpperCase()}.
                </p>
                <p className="mt-2">
                  Ne sont pas couverts : les dégradations dues au vent, à un mauvais usage, à un défaut d'entretien, à un montage défectueux, à un stockage inadapté, à des agressions extérieures (pollution, animaux, chocs).
                </p>
              </section>

              {/* Section 5 */}
              <section>
                <h2 className="text-2xl font-bold text-brand mb-3">5. COMMANDE</h2>
                <p>
                  Le Client passe commande sur le Site en suivant le processus de commande en ligne. Avant de valider, il peut vérifier le détail de sa commande et corriger d'éventuelles erreurs. La commande devient ferme après validation du paiement. ${brand.name.toUpperCase()} se réserve le droit de refuser toute commande anormale ou passée de mauvaise foi. En cas d'indisponibilité exceptionnelle d'un coloris ou d'un tissu, le Client sera informé et une solution (remplacement ou remboursement de l'article concerné) lui sera proposée.
                </p>
              </section>

              {/* Section 6 */}
              <section>
                <h2 className="text-2xl font-bold text-brand mb-3">6. PRIX</h2>
                <p>
                  Les prix sont indiqués en euros (€). Ils peuvent être modifiés à tout moment, mais le prix appliqué est celui indiqué au moment de la commande. Les prix ne comprennent pas nécessairement les frais de livraison, indiqués et ajoutés avant validation définitive. En cas d'erreur manifeste de prix, ${brand.name.toUpperCase()} pourra annuler la commande.
                </p>
              </section>

              {/* Section 7 */}
              <section>
                <h2 className="text-2xl font-bold text-brand mb-3">7. MODALITÉS DE PAIEMENT</h2>
                <p className="mb-2">
                  Le paiement est exigible en totalité à la commande.
                </p>
                <p className="mb-2"><strong>Moyens acceptés :</strong></p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Carte bancaire (via solution sécurisée type Stripe / 3D Secure)</li>
                  <li>Virement bancaire</li>
                  <li>PayPal</li>
                  <li>Chèque bancaire français (traitement après encaissement effectif)</li>
                  <li>Paiement en plusieurs fois par prestataire sécurisé, lorsque cette option est proposée sur le Site</li>
                </ul>
                <p className="mt-2">
                  La commande ne sera préparée qu'après validation ou encaissement du paiement. En cas de refus de la banque ou du prestataire de paiement, la commande est automatiquement annulée.
                </p>
              </section>

              {/* Section 8 */}
              <section>
                <h2 className="text-2xl font-bold text-brand mb-3">8. DÉLAIS DE FABRICATION ET DE LIVRAISON</h2>
                <div className="space-y-3">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-1">8.1 Délais de fabrication</h3>
                    <p>
                      Les produits sur mesure (toiles de store, lambrequins, flocages, éléments lumineux) sont fabriqués à la commande. Le délai moyen constaté est de 10 à 15 jours ouvrés à compter de la validation de la commande et du paiement. Ce délai est indicatif et peut varier selon la période (saison, congés, forte activité, rupture tissu).
                    </p>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-1">8.2 Zones de livraison</h3>
                    <p>
                      Livraison en France métropolitaine, Corse, Union européenne, Suisse, DOM-TOM et autres destinations : selon tarif affiché au panier ou sur devis spécifique.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-1">8.3 Transport et réception</h3>
                    <p>
                      La livraison est effectuée à l'adresse indiquée par le Client, en rez-de-chaussée ou en limite de propriété. Le Client doit vérifier l'état du colis à l'arrivée. En cas d'avarie ou de produit manquant, il doit émettre des réserves précises sur le bon du transporteur et en informer ${brand.name.toUpperCase()} sous 48 h, photos à l'appui. À défaut, la livraison sera réputée conforme.
                    </p>
                  </div>
                </div>
              </section>

              {/* Section 9 */}
              <section>
                <h2 className="text-2xl font-bold text-brand mb-3">9. DROIT DE RÉTRACTATION</h2>
                <div className="space-y-3">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-1">9.1 Produits standards</h3>
                    <p>
                      Pour les produits non personnalisés, le Client consommateur dispose d'un délai de 14 jours à compter de la réception pour exercer son droit de rétractation, conformément aux articles L221-18 et suivants du Code de la consommation. Les frais de retour sont à la charge du Client. Le produit doit être retourné neuf, complet, dans son emballage d'origine.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-1">9.2 Produits sur mesure / personnalisés</h3>
                    <p>
                      Conformément à l'article L221-28 du Code de la consommation, le droit de rétractation ne peut pas être exercé pour les biens confectionnés selon les spécifications du consommateur ou nettement personnalisés. Sont donc exclus du droit de rétractation : les toiles de stores bannes coupées aux dimensions du Client, les lambrequins sur mesure, les lambrequins lumineux préparés à la demande, les marquages, les produits floqués ou imprimés, et d'une manière générale tout produit fabriqué spécialement pour le Client. Ces produits ne sont ni repris ni échangés, sauf erreur avérée de ${brand.name.toUpperCase()}.
                    </p>
                  </div>
                </div>
              </section>

              {/* Section 10 */}
              <section>
                <h2 className="text-2xl font-bold text-brand mb-3">10. GARANTIES</h2>
                <p>
                  Le Client bénéficie des garanties légales de conformité (articles L217-3 et suivants du Code de la consommation) et contre les vices cachés (articles 1641 et suivants du Code civil). Certaines toiles ou tissus peuvent bénéficier d'une garantie fabricant spécifique (durée et conditions propres au fabricant : tenue des couleurs, imputrescibilité, etc.). La garantie ne couvre pas :
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
                  <li>une erreur de prise de mesure du Client,</li>
                  <li>une pose non conforme,</li>
                  <li>l'usure normale,</li>
                  <li>un usage anormal (store laissé ouvert par vent fort),</li>
                  <li>un défaut d'entretien.</li>
                </ul>
                <p className="mt-2">
                  Avant tout retour, le Client doit contacter le service client à {brand.contact.email}.
                </p>
              </section>

              {/* Section 11 */}
              <section>
                <h2 className="text-2xl font-bold text-brand mb-3">11. RESPONSABILITÉ</h2>
                <p>
                  ${brand.name.toUpperCase()} ne peut être tenue responsable des dommages indirects ou immatériels (perte d'exploitation, perte de chance…). La responsabilité du vendeur ne peut pas être engagée en cas d'inexécution ou de mauvaise exécution due au Client, au fait imprévisible et insurmontable d'un tiers ou à un cas de force majeure (grève des transports, catastrophes naturelles, panne générale d'Internet, etc.).
                </p>
              </section>

              {/* Section 12 */}
              <section>
                <h2 className="text-2xl font-bold text-brand mb-3">12. SERVICE CLIENT</h2>
                <p>Pour toute question ou réclamation :</p>
                <div className="mt-2 space-y-1">
                  <p><strong>{brand.name.toUpperCase()} – Service Client</strong></p>
                  <p>{brand.contact.address.street} – {brand.contact.address.postalCode} {brand.contact.address.city}</p>
                  <p>E-mail : {brand.contact.email}</p>
                  <p>Tél. : {brand.contact.phone}</p>
                </div>
              </section>

              {/* Section 13 */}
              <section>
                <h2 className="text-2xl font-bold text-brand mb-3">13. MÉDIATION DE LA CONSOMMATION</h2>
                <p>
                  Conformément aux articles L612-1 et suivants du Code de la consommation, le Client consommateur peut saisir gratuitement un médiateur de la consommation après avoir d'abord adressé sa réclamation écrite au service client et en l'absence de solution amiable.
                </p>
                <p className="mt-2">
                  <strong>Médiateur désigné :</strong><br />
                  AME CONSO (Association des Médiateurs Européens), référencée par la CECMC. Le consommateur peut la saisir en ligne ou par courrier, dans un délai d'un an à compter de sa réclamation. (<a href="https://www.economie.gouv.fr/mediation-conso/vous-etes-un-professionnel/choisir-un-mediateur-de-la-consommation/mediateurs-references" className="text-brand hover:underline" target="_blank" rel="noopener noreferrer">Ministère des Finances</a>)
                </p>
                <p className="mt-2">
                  Le médiateur tentera de rapprocher les parties en vue d'une solution amiable. Le recours à la médiation est gratuit pour le consommateur.
                </p>
              </section>

              {/* Section 14 */}
              <section>
                <h2 className="text-2xl font-bold text-brand mb-3">14. DONNÉES PERSONNELLES</h2>
                <p>
                  Les données collectées lors de la commande sont nécessaires à son traitement, à la facturation et à la livraison. Elles peuvent également servir à l'envoi d'informations commerciales de {brand.name.toUpperCase()}. Le Client peut exercer ses droits d'accès, de rectification et d'opposition en écrivant à {brand.contact.email}.
                </p>
              </section>

              {/* Section 15 */}
              <section>
                <h2 className="text-2xl font-bold text-brand mb-3">15. PROPRIÉTÉ INTELLECTUELLE</h2>
                <p>
                  Tous les éléments du Site (textes, images, logos, marques, nom {brand.name.toUpperCase()}) sont protégés au titre de la propriété intellectuelle. Toute reproduction ou utilisation non autorisée est interdite.
                </p>
              </section>

              {/* Section 16 */}
              <section>
                <h2 className="text-2xl font-bold text-brand mb-3">16. LOI APPLICABLE – TRIBUNAL COMPÉTENT</h2>
                <p>
                  Les présentes CGV sont soumises au droit français. À défaut de règlement amiable ou de médiation aboutie, tout litige sera porté devant les tribunaux compétents du ressort de Draguignan.
                </p>
              </section>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CGVPage;